<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import {
  adminGetOcrMetrics,
  type OcrMetricRow,
  type OcrMetricsOut,
} from '../api';

const data = ref<OcrMetricsOut | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const days = ref(30);

async function load() {
  loading.value = true;
  error.value = null;
  try {
    data.value = await adminGetOcrMetrics(days.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(days, load);

function pct(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(0)}%`;
}

function num(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return v.toFixed(2);
}

function engineLabel(e: string): string {
  if (e === 'tesseract') return 'Tesseract';
  if (e === 'yandex_vision') return 'Yandex Vision';
  if (e === 'gigachat') return 'GigaChat';
  return e;
}

function fieldLabel(f: string): string {
  if (f === 'client_name') return 'ФИО';
  if (f === 'phone') return 'Телефон';
  if (f === 'address') return 'Адрес';
  if (f === 'amount') return 'Сумма';
  return f;
}

function recallColor(v: number | null): string {
  if (v === null) return 'text-hint';
  if (v >= 0.8) return 'text-green-600 font-semibold';
  if (v >= 0.5) return 'text-yellow-600';
  return 'text-red-500';
}

// Группируем строки по полю для удобства сравнения движков
function rowsByField(rows: OcrMetricRow[]): { field: string; rows: OcrMetricRow[] }[] {
  const groups: Record<string, OcrMetricRow[]> = {};
  for (const r of rows) {
    if (!groups[r.field_name]) groups[r.field_name] = [];
    groups[r.field_name].push(r);
  }
  const order = ['client_name', 'phone', 'address', 'amount'];
  return order
    .filter((f) => groups[f])
    .map((f) => ({ field: f, rows: groups[f] }));
}
</script>

<template>
  <section class="flex flex-col gap-3 p-2 admin-tab-content">
    <header class="flex justify-between items-center">
      <h2 class="text-lg font-semibold">Точность OCR</h2>
      <label class="text-xs">
        За
        <select v-model.number="days" class="input inline-block w-auto py-1">
          <option :value="7">7 дней</option>
          <option :value="30">30 дней</option>
          <option :value="90">90 дней</option>
        </select>
      </label>
    </header>

    <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>
    <p v-if="loading" class="text-hint text-sm">Загрузка…</p>

    <div v-if="data && !loading" class="text-xs text-hint">
      Всего распознаваний: <span class="font-mono">{{ data.total_samples }}</span>
      &middot; Движков: <span class="font-mono">{{ data.engine_versions.length }}</span>
    </div>

    <div v-if="data && data.rows.length === 0 && !loading" class="text-hint text-sm text-center py-8">
      Нет данных за выбранный период.
      <br>
      Если только что подключили cloud OCR — после первых сканов появятся
      строки tesseract, через 1-2 дня (когда выездной введёт ФИО / Б24 поймает сделку) появится recall.
    </div>

    <!-- Сравнение по полям: для каждого поля показываем все движки -->
    <div v-for="group in (data ? rowsByField(data.rows) : [])" :key="group.field" class="border rounded p-2 flex flex-col gap-1">
      <div class="font-semibold text-sm">{{ fieldLabel(group.field) }}</div>
      <table class="w-full text-xs">
        <thead class="text-hint">
          <tr>
            <th class="text-left">Движок</th>
            <th class="text-right">Образцов</th>
            <th class="text-right">Сверено</th>
            <th class="text-right">Попаданий</th>
            <th class="text-right">Похожесть</th>
            <th class="text-right">Точно</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in group.rows" :key="r.engine" class="border-t">
            <td class="py-1">{{ engineLabel(r.engine) }}</td>
            <td class="text-right font-mono">{{ r.total_samples }}</td>
            <td class="text-right font-mono">{{ r.with_ground_truth }}</td>
            <td :class="['text-right font-mono', recallColor(r.recall)]">{{ pct(r.recall) }}</td>
            <td class="text-right font-mono">{{ num(r.avg_similarity) }}</td>
            <td class="text-right font-mono">{{ pct(r.exact_match_rate) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <details v-if="data && data.engine_versions.length" class="text-xs text-hint">
      <summary class="cursor-pointer">Версии движков</summary>
      <ul class="ml-4 mt-1">
        <li v-for="v in data.engine_versions" :key="`${v.engine}-${v.version ?? ''}`">
          {{ engineLabel(v.engine) }}: <span class="font-mono">{{ v.version ?? 'версия неизвестна' }}</span>
        </li>
      </ul>
    </details>
  </section>
</template>
